import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Form } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import CreditCardForm from '../../../views/billing/billing-method/CreditCardForm';
import ErrorAlert from '../../Alert/ErrorAlert';
import Spinner from '../../Spinner';
import SubmitCancelButtonGroup from '../../SubmitCancelButtonGroup';
import GenericModal from '../GenericModal';

const CreditCardModalContent = props => {
  const {
    t,
    visible,
    initialValues,
    onCancel,
    onSubmit,
    isLoading,
    isUpdate,
    stripeLoadError,
  } = props;
  const stripe = useStripe();
  const elements = useElements();
  const [form] = Form.useForm();
  const [cardReady, setCardReady] = useState(false);
  const [cardError, setCardError] = useState();
  const [isTokenizing, setIsTokenizing] = useState(false);
  const submitting = useRef(false);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const isSubmitting = isLoading || isTokenizing;

  const formInitialValues = isUpdate
    ? {
        name: initialValues.name,
        cardNumber: `XXXX XXXX XXXX ${initialValues.last4}`,
        expirationDate: `${String(initialValues.expMonth).padStart(2, '0')} / ${String(
          initialValues.expYear,
        ).slice(-2)}`,
      }
    : { isDefault: true };

  const handleSubmit = async () => {
    if (submitting.current || isLoading) return;
    submitting.current = true;
    try {
      let values;
      try {
        values = await form.validateFields();
      } catch {
        return;
      }

      if (isUpdate) {
        await onSubmit(values);
        return;
      }

      const card = elements?.getElement(CardElement);
      if (!stripe || !card || !cardReady) return;
      setCardError();
      setIsTokenizing(true);

      let result;
      try {
        result = await stripe.createToken(card, { name: values.name });
      } catch {
        setCardError(t('cardTokenizationError'));
        return;
      }
      if (result.error || !result.token?.id) {
        setCardError(result.error?.message || t('cardTokenizationError'));
        return;
      }
      await onSubmit({
        tokenId: result.token.id,
        isDefault: !!values.isDefault,
      });
    } catch {
      // API failures are displayed by the billing mutation's existing error handler.
    } finally {
      submitting.current = false;
      if (mounted.current) setIsTokenizing(false);
    }
  };

  return (
    <GenericModal
      centered
      destroyOnClose
      title={t(isUpdate ? 'updateCreditCard' : 'addCreditCard')}
      visible={visible}
      onCancel={isSubmitting ? undefined : onCancel}
      closable={!isSubmitting}
      bodyStyle={{ overflowY: 'auto' }}
      width={700}
      footer={
        <SubmitCancelButtonGroup
          loading={isSubmitting}
          disabled={isSubmitting}
          submitDisabled={!isUpdate && (!stripe || !cardReady || stripeLoadError)}
          onCancel={onCancel}
          onSubmit={handleSubmit}
          submitText={isUpdate ? t('update') : t('add')}
        />
      }
    >
      {stripeLoadError && <ErrorAlert message={t('stripeCardLoadError')} />}
      <Spinner spinning={!isUpdate && !cardReady && !stripeLoadError}>
        <CreditCardForm
          t={t}
          isUpdate={isUpdate}
          formInstance={form}
          initialValues={formInitialValues}
          isSubmitting={isSubmitting}
          onCardReady={() => setCardReady(true)}
          onCardChange={event => setCardError(event.error?.message)}
          cardError={cardError}
        />
      </Spinner>
    </GenericModal>
  );
};

const CreditCardFormModal = props => {
  const { visible, isUpdate } = props;
  const [stripeLoadError, setStripeLoadError] = useState(false);
  const stripePromise = useMemo(() => {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!visible || isUpdate || !/^pk_(test|live)_/.test(key || '')) return null;
    return loadStripe(key).catch(() => null);
  }, [visible, isUpdate]);

  useEffect(() => {
    let active = true;
    setStripeLoadError(false);
    if (visible && !isUpdate) {
      if (!stripePromise) {
        setStripeLoadError(true);
      } else {
        stripePromise.then(stripe => {
          if (active) setStripeLoadError(!stripe);
        });
      }
    }
    return () => {
      active = false;
    };
  }, [visible, isUpdate, stripePromise]);

  if (!visible) return null;

  return (
    <Elements key={isUpdate ? 'edit' : 'add'} stripe={stripePromise}>
      <CreditCardModalContent {...props} stripeLoadError={stripeLoadError} />
    </Elements>
  );
};

export default CreditCardFormModal;
